import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, ShieldCheck, ArrowLeft, CheckCircle, Building2,
  MessageSquare, Loader2, ChevronRight, Gift, TrendingUp, Store,
  Percent, Star, AlertTriangle, Wallet, ClipboardList, BadgeCheck,
  Bot, Sparkles, X, UserCircle, Plus, Languages, Award, Fingerprint, IdCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import AgentTypeBadge from '../components/AgentTypeBadge';
import DynamicTaskForm from '../components/DynamicTaskForm';
import MyRequests from './MyRequests';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_LIGHT = '/cloudgm-logo.png';

export default function AgentTasks() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [agentInfo, setAgentInfo] = useState(null);
  const [adminTasks, setAdminTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // Earnings summary (from admin service_requests aggregation)
  const [earnings, setEarnings] = useState({
    total_commissions: 0,
    registered_companies: 0,
    available_balance: 0,
  });

  // Task details panel (inline drawer, not blocking modal)
  const [selectedTask, setSelectedTask] = useState(null);

  // Form task modal (kept — dynamic form flow)
  const [dynamicFormTask, setDynamicFormTask] = useState(null);

  // Tabs: 'info' | 'tasks' | 'completed' | 'commission'
  const [activeTab, setActiveTab] = useState('info');

  // Agent profile edit state (append-only)
  const [newSkill, setNewSkill] = useState('');
  const [newLang, setNewLang] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchAgentInfo();
    fetchAdminTasks();
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const token = localStorage.getItem('business_token');
      const res = await axios.get(`${BACKEND_URL}/api/agent/earnings-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEarnings(res.data || {});
    } catch (e) {
      // fail-open — keep zeros
    }
  };

  const fetchAdminTasks = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) return;
    setTasksLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch admin tasks:', error);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchAgentInfo = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.length > 0) {
        const approvedRequest = response.data.find(req => req.status === 'approved');
        if (!approvedRequest) {
          navigate('/dashboard');
          return;
        }
        setAgentInfo(approvedRequest);
      } else {
        navigate('/become-agent');
      }
    } catch (error) {
      console.error('Failed to fetch agent info:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const appendSkill = async () => {
    const v = newSkill.trim();
    if (!v) return;
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/agent-requests/me/append-profile`,
        { new_skills: [v], new_languages: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentInfo(prev => ({ ...prev, skills: data.skills, languages: data.languages }));
      setNewSkill('');
      toast.success(language === 'ar' ? '✅ تمت إضافة المهارة' : '✅ Skill added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const appendLanguage = async () => {
    const v = newLang.trim();
    if (!v) return;
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/agent-requests/me/append-profile`,
        { new_skills: [], new_languages: [v] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentInfo(prev => ({ ...prev, skills: data.skills, languages: data.languages }));
      setNewLang('');
      toast.success(language === 'ar' ? '✅ تمت إضافة اللغة' : '✅ Language added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'ar' ? 'يجب أن يكون الملف صورة' : 'File must be an image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'حجم الصورة أكبر من 5 ميجابايت' : 'Image larger than 5MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('business_token');
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/agent-requests/me/profile-photo`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setAgentInfo(prev => ({ ...prev, profile_photo: data.profile_photo }));
      toast.success(language === 'ar' ? '✅ تم تحديث الصورة' : '✅ Photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (language === 'ar' ? 'فشل رفع الصورة' : 'Upload failed'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const startTask = (task) => {
    const mode = task.mode || (task.uses_ai_agent ? 'ai' : 'form');
    if (mode === 'form') {
      setDynamicFormTask(task);
      setSelectedTask(null);
    } else if (mode === 'ai') {
      // Navigate to full-page AI chat
      const taskId = task.id;
      navigate(`/agent-tasks/chat/${taskId}`);
    } else {
      toast.error(language === 'ar' ? 'نوع المهمة غير مدعوم' : 'Unsupported task type');
    }
  };

  const startFetchCompanies = () => {
    // Legacy generic "fetch companies" — use special sentinel id
    navigate('/agent-tasks/chat/fetch-companies');
  };

  const agentBenefits = [
    { icon: Percent,     benefit: language === 'ar' ? 'عمولة على كل شركة'          : 'Commission per company',   value: language === 'ar' ? 'من 1% إلى 5%' : '1% to 5%',   color: 'text-green-500' },
    { icon: Percent,     benefit: language === 'ar' ? 'عمولة على خدمات الطباعة'    : 'Printing services commission', value: language === 'ar' ? 'من 1% إلى 2%' : '1% to 2%', color: 'text-[#1E9BD9]' },
    { icon: TrendingUp,  benefit: language === 'ar' ? 'نسبة من المبيعات'            : 'Sales percentage',           value: language === 'ar' ? 'من 1% إلى 2%' : '1% to 2%',   color: 'text-blue-500' },
    { icon: TrendingUp,  benefit: language === 'ar' ? 'نسبة من الخدمات الأخرى'      : 'Other services percentage',  value: language === 'ar' ? 'من 1% إلى 2%' : '1% to 2%',   color: 'text-purple-500' },
  ];

  // Real completed tasks come from admin platform once a request's full stage
  // pipeline finishes (commission collected → sent). No dummy data here.
  const completedTasks = [];

  const commissionSummary = {
    total: earnings.total_commissions || 0,
    companies: earnings.registered_companies || 0,
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  const totalTasksCount = adminTasks.length + 1; // +1 for the built-in Fetch Companies card

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* HERO — compact, mode-aware (slate for dark; light cyan gradient for light) */}
      <section className={`relative overflow-hidden border-b ${isDarkMode ? 'bg-[#0F172A] border-white/5' : 'bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] border-transparent'}`}>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: isDarkMode
          ? 'radial-gradient(circle at 20% 20%, rgba(56,189,248,0.25) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(14,165,233,0.18) 0%, transparent 40%)'
          : 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.15) 0%, transparent 45%)'
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Back button + title */}
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                className="text-white/85 hover:text-white hover:bg-white/10 h-8 px-2 text-sm flex-shrink-0"
                onClick={() => navigate('/dashboard')}
                data-testid="agent-tasks-back-btn"
              >
                <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
                {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
              </Button>
              <div className="min-w-0 hidden sm:block">
                <h1 className="text-base sm:text-lg font-bold text-white flex items-center gap-1.5 truncate">
                  {language === 'ar' ? 'مساحة الوكيل' : 'Agent Workspace'}
                  <BadgeCheck className="h-4 w-4 text-cyan-300 flex-shrink-0" />
                </h1>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {[
              { id: 'info', label: language === 'ar' ? 'معلومات الوكيل' : 'Agent Info', icon: IdCard },
              { id: 'tasks', label: language === 'ar' ? 'المهام المتاحة' : 'Available Tasks', count: totalTasksCount, icon: Briefcase },
              { id: 'my-requests', label: language === 'ar' ? 'مهامي' : 'My Tasks', icon: ClipboardList },
              { id: 'commission', label: language === 'ar' ? 'العمولات والمكافآت' : 'Commission & Rewards', icon: Wallet },
            ].map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`agent-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all border ${
                    active
                      ? 'bg-white text-[#0F172A] border-white shadow-lg'
                      : 'bg-white/10 text-white/85 border-white/20 hover:bg-white/20'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-cyan-100 text-cyan-700' : 'bg-white/20 text-white'}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* AGENT INFO TAB */}
        {activeTab === 'info' && (
          <div data-testid="agent-tab-content-info">
            {/* 2×2 grid — all 4 cards same size:
                Row 1: [Basic Info] [Skills]
                Row 2: [Agent ID Card] [Languages] */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
              {/* Basic Info */}
              <div className={`rounded-2xl p-5 border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <UserCircle className="h-5 w-5 text-emerald-500" />
                  {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoRow label={language === 'ar' ? 'الاسم الكامل' : 'Full Name'} value={agentInfo?.account_name || agentInfo?.fullNameAr || agentInfo?.fullName} isDarkMode={isDarkMode} />
                  <InfoRow label={language === 'ar' ? 'رقم الوكيل' : 'Agent Number'} value={agentInfo?.account_bgm_number || agentInfo?.bgm_number} mono isDarkMode={isDarkMode} />
                  <InfoRow label={language === 'ar' ? 'نوع الوكيل' : 'Agent Type'} value={agentInfo?.agent_type} isDarkMode={isDarkMode} badge />
                  <InfoRow
                    label={language === 'ar' ? 'الحالة' : 'Status'}
                    value={language === 'ar' ? 'معتمد ✓' : 'Verified ✓'}
                    isDarkMode={isDarkMode}
                    valueClass="text-emerald-500 font-semibold"
                  />
                  <InfoRow
                    label={language === 'ar' ? 'طريقة الانضمام' : 'Invitation Method'}
                    value={
                      agentInfo?.registrationType === 'invitation' || agentInfo?.invitationCode
                        ? (language === 'ar' ? `عبر دعوة${agentInfo?.invitationCode ? ` — ${agentInfo.invitationCode}` : ''}` : `Invitation${agentInfo?.invitationCode ? ` — ${agentInfo.invitationCode}` : ''}`)
                        : (language === 'ar' ? 'تقديم مباشر' : 'Direct application')
                    }
                    isDarkMode={isDarkMode}
                  />
                  {agentInfo?.nationality && <InfoRow label={language === 'ar' ? 'الجنسية' : 'Nationality'} value={agentInfo.nationality} isDarkMode={isDarkMode} />}
                  {agentInfo?.city && <InfoRow label={language === 'ar' ? 'الإمارة/المدينة' : 'Emirate/City'} value={agentInfo.city} isDarkMode={isDarkMode} />}
                  {agentInfo?.experience && <InfoRow label={language === 'ar' ? 'سنوات الخبرة' : 'Years of Experience'} value={agentInfo.experience} isDarkMode={isDarkMode} />}
                </div>
              </div>

              {/* Skills */}
              <div className={`rounded-2xl p-5 border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <Award className="h-5 w-5 text-amber-500" />
                    {language === 'ar' ? 'المهارات' : 'Skills'}
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                    {(agentInfo?.skills || []).length}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-3 min-h-[40px]" data-testid="agent-skills-list">
                  {(agentInfo?.skills || []).length > 0 ? (
                    (agentInfo?.skills || []).map((s, i) => (
                      <span key={i} className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full ${isDarkMode ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                        <Sparkles className="h-3 w-3" />
                        {s}
                      </span>
                    ))
                  ) : (
                    <p className={`text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {language === 'ar' ? 'لا توجد مهارات مسجلة بعد' : 'No skills yet'}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-dashed border-gray-200/50 dark:border-white/10">
                  <Input
                    data-testid="agent-add-skill-input"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && appendSkill()}
                    placeholder={language === 'ar' ? 'إضافة مهارة جديدة...' : 'Add a new skill...'}
                    className={`flex-1 h-9 text-sm ${isDarkMode ? 'bg-white/5 border-white/10 text-white' : ''}`}
                    disabled={savingProfile}
                  />
                  <Button
                    onClick={appendSkill}
                    disabled={!newSkill.trim() || savingProfile}
                    size="sm"
                    className="bg-gradient-to-r from-emerald-500 to-green-600 text-white h-9"
                    data-testid="agent-add-skill-btn"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? '🔒 المهارات الحالية ثابتة — يمكنك فقط الإضافة.' : '🔒 Existing skills are locked — you can only add new ones.'}
                </p>
              </div>

              {/* Agent ID Card */}
              <AgentIdCard
                agentInfo={agentInfo}
                language={language}
                isDarkMode={isDarkMode}
                onPhotoChange={handlePhotoUpload}
                uploadingPhoto={uploadingPhoto}
              />

              {/* Languages */}
              <div className={`rounded-2xl p-5 border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <Languages className="h-5 w-5 text-blue-500" />
                    {language === 'ar' ? 'اللغات' : 'Languages'}
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                    {(agentInfo?.languages || []).length}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-3 min-h-[40px]" data-testid="agent-languages-list">
                  {(agentInfo?.languages || []).length > 0 ? (
                    (agentInfo?.languages || []).map((l, i) => (
                      <span key={i} className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full uppercase ${isDarkMode ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                        <Languages className="h-3 w-3" />
                        {l}
                      </span>
                    ))
                  ) : (
                    <p className={`text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {language === 'ar' ? 'لا توجد لغات مسجلة بعد' : 'No languages yet'}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-dashed border-gray-200/50 dark:border-white/10">
                  <Input
                    data-testid="agent-add-language-input"
                    value={newLang}
                    onChange={(e) => setNewLang(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && appendLanguage()}
                    placeholder={language === 'ar' ? 'إضافة لغة جديدة (مثال: fr, es)...' : 'Add a new language (e.g., fr, es)...'}
                    className={`flex-1 h-9 text-sm ${isDarkMode ? 'bg-white/5 border-white/10 text-white' : ''}`}
                    disabled={savingProfile}
                  />
                  <Button
                    onClick={appendLanguage}
                    disabled={!newLang.trim() || savingProfile}
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white h-9"
                    data-testid="agent-add-language-btn"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? '🔒 اللغات الحالية ثابتة — يمكنك فقط الإضافة.' : '🔒 Existing languages are locked — you can only add new ones.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TASKS TAB */}
        {activeTab === 'tasks' && (
          <div data-testid="agent-tab-content-tasks">
            <div className="mb-4 flex items-center justify-between">
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {language === 'ar' ? `المهام المتاحة (${totalTasksCount})` : `Available Tasks (${totalTasksCount})`}
              </h2>
              {tasksLoading && <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="agent-tasks-grid">
              {/* Admin tasks */}
              {adminTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  language={language}
                  isDarkMode={isDarkMode}
                  onView={() => setSelectedTask(task)}
                  onStart={() => startTask(task)}
                />
              ))}

              {/* Built-in Fetch Companies card */}
              <div
                data-testid="agent-task-card-fetch-companies"
                className={`group rounded-2xl p-5 cursor-pointer transition-all border flex flex-col relative overflow-hidden ${
                  isDarkMode
                    ? 'bg-[#1E293B] border-white/10 hover:border-blue-500/50'
                    : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-xl'
                }`}
                onClick={startFetchCompanies}
              >
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-blue-500/15 to-cyan-500/15 group-hover:scale-125 transition-transform" />
                <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-3">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <span className={`self-start text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 mb-2 ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                  <Bot className="h-3 w-3" />
                  {language === 'ar' ? 'مساعد ذكي' : 'AI Assist'}
                </span>
                <h4 className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'جلب الشركات' : 'Fetch Companies'}
                </h4>
                <p className={`text-sm mt-2 line-clamp-2 flex-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أضف شركات جديدة واحصل على مكافآت لكل شركة معتمدة' : 'Add new companies and earn rewards for each approved one'}
                </p>
                <div className={`flex items-center justify-between mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                  <span className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {language === 'ar' ? 'ابدأ الآن' : 'Start now'}
                  </span>
                  <ChevronRight className={`h-4 w-4 rtl:rotate-180 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
              </div>
            </div>

            {adminTasks.length === 0 && !tasksLoading && (
              <div className={`text-center py-8 mt-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{language === 'ar' ? 'لا توجد مهام إضافية من الإدارة حالياً' : 'No extra admin tasks at the moment'}</p>
              </div>
            )}
          </div>
        )}

        {/* MY REQUESTS TAB — embedded MyRequests without its own hero */}
        {activeTab === 'my-requests' && (
          <div data-testid="agent-tab-content-my-requests">
            <MyRequests embedded />
          </div>
        )}

        {/* COMMISSION TAB */}
        {activeTab === 'commission' && (
          <div data-testid="agent-tab-content-commission">
            <h2 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {language === 'ar' ? 'ملخص الأرباح والعمولات' : 'Earnings & Commissions'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <StatCard label={language === 'ar' ? 'إجمالي العمولات' : 'Total Commissions'} value={commissionSummary.total} unit={language === 'ar' ? 'درهم' : 'AED'} color="green" isDarkMode={isDarkMode} />
              <StatCard label={language === 'ar' ? 'الشركات المسجلة' : 'Registered Companies'} value={commissionSummary.companies} unit={language === 'ar' ? 'شركة' : 'firms'} color="purple" isDarkMode={isDarkMode} />
            </div>

            <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
              <h3 className={`font-bold text-base mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {language === 'ar' ? 'المميزات والمكافآت' : 'Benefits & Rewards'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {agentBenefits.map((item, index) => (
                  <div key={index} className={`flex items-center gap-3 p-3 rounded-xl cgm-page`}>
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-white'}`}>
                      <item.icon className={`h-5 w-5 ${item.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.benefit}</p>
                      <p className={`font-bold ${item.color}`}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Task Details Side Drawer (non-blocking modal alternative) */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex" data-testid="task-details-drawer">
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedTask(null)} />
          <div className={`w-full sm:max-w-md h-full overflow-y-auto ${isDarkMode ? 'bg-[#0F172A] border-s border-white/10' : 'bg-white border-s border-gray-200'} shadow-2xl`}>
            <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-500 to-green-600 p-5 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold pe-2 line-clamp-2">
                {language === 'ar' ? selectedTask.title_ar : selectedTask.title_en || selectedTask.title_ar}
              </h3>
              <button onClick={() => setSelectedTask(null)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center flex-shrink-0" data-testid="task-details-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Mode badge */}
              <div className="flex items-center gap-2">
                {(selectedTask.uses_ai_agent || selectedTask.mode === 'ai') ? (
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                    <Bot className="h-3.5 w-3.5" />
                    {language === 'ar' ? 'مساعد ذكي' : 'AI-guided'}
                  </span>
                ) : (
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                    <ClipboardList className="h-3.5 w-3.5" />
                    {language === 'ar' ? 'نموذج' : 'Form'}
                  </span>
                )}
                {selectedTask.has_custom_tiers && (
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    <Star className="h-3.5 w-3.5" />
                    {language === 'ar' ? 'نسبة مخصصة' : 'Custom rate'}
                  </span>
                )}
              </div>

              {/* Profit tiers */}
              {(selectedTask.profit_tiers?.length > 0 || selectedTask.profit_percentage) && (
                <div className="space-y-2" data-testid="task-tiers-list">
                  {(selectedTask.profit_tiers?.length ? selectedTask.profit_tiers : [{ percentage: selectedTask.profit_percentage, description_ar: selectedTask.profit_details, description_en: selectedTask.profit_details }]).map((tier, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-green-500/15' : 'bg-green-50'}`}>
                      <Percent className={`h-6 w-6 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {tier.percentage}%
                        </p>
                        {(language === 'ar' ? tier.description_ar : tier.description_en || tier.description_ar) && (
                          <p className={`text-xs ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                            {language === 'ar' ? tier.description_ar : tier.description_en || tier.description_ar}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Description */}
              <div>
                <h4 className={`font-semibold text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {language === 'ar' ? 'الوصف' : 'Description'}
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>
                  {language === 'ar' ? selectedTask.description_ar : selectedTask.description_en || selectedTask.description_ar}
                </p>
              </div>

              {/* Warnings */}
              {(selectedTask.warnings_ar || selectedTask.warnings_en || selectedTask.warnings) && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                    <div>
                      <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                        {language === 'ar' ? 'ملاحظات هامة' : 'Important Notes'}
                      </h4>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>
                        {language === 'ar'
                          ? (selectedTask.warnings_ar || selectedTask.warnings)
                          : (selectedTask.warnings_en || selectedTask.warnings_ar || selectedTask.warnings)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                className="w-full mt-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white h-11"
                data-testid="task-start-btn"
                onClick={() => startTask(selectedTask)}
              >
                <MessageSquare className="h-5 w-5 me-2" />
                {(selectedTask.mode === 'form' || (!selectedTask.mode && !selectedTask.uses_ai_agent))
                  ? (language === 'ar' ? 'تعبئة النموذج' : 'Fill Form')
                  : (language === 'ar' ? 'ابدأ المهمة' : 'Start Task')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Form Modal (form-mode tasks) */}
      {dynamicFormTask && (
        <DynamicTaskForm
          task={dynamicFormTask}
          language={language}
          isDarkMode={isDarkMode}
          onClose={() => setDynamicFormTask(null)}
          onSuccess={() => setDynamicFormTask(null)}
        />
      )}
    </div>
  );
}

// ================== Sub-components ==================

function TaskCard({ task, language, isDarkMode, onView, onStart }) {
  const isAI = task.uses_ai_agent || task.mode === 'ai';
  const isForm = task.mode === 'form' && (task.form_schema?.length > 0);
  const accentGradient = isAI ? 'from-blue-500 to-cyan-500' : 'from-emerald-500 to-green-600';
  const accentHover = isAI ? 'hover:border-blue-500/50' : 'hover:border-emerald-500/50';

  return (
    <div
      data-testid={`agent-task-card-${task.id}`}
      className={`group rounded-2xl p-5 transition-all border flex flex-col relative overflow-hidden ${
        isDarkMode
          ? `bg-[#1E293B] border-white/10 ${accentHover}`
          : `bg-white border-gray-200 hover:shadow-xl ${accentHover.replace('/50', '')}`
      }`}
    >
      <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br ${accentGradient} opacity-10 group-hover:opacity-20 group-hover:scale-125 transition-all`} />

      <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${accentGradient} flex items-center justify-center mb-3`}>
        <Briefcase className="h-6 w-6 text-white" />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {isAI && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`} data-testid="task-ai-badge">
            <Bot className="h-3 w-3" />
            {language === 'ar' ? 'مساعد ذكي' : 'AI Assist'}
          </span>
        )}
        {isForm && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`} data-testid="task-form-badge">
            <ClipboardList className="h-3 w-3" />
            {language === 'ar' ? 'نموذج' : 'Form'}
          </span>
        )}
        {task.has_custom_tiers && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
            <Star className="h-3 w-3" />
            {language === 'ar' ? 'نسبة مخصصة' : 'Custom rate'}
          </span>
        )}
      </div>

      <h4 className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        {language === 'ar' ? task.title_ar : task.title_en || task.title_ar}
      </h4>

      {(task.profit_tiers?.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {task.profit_tiers.slice(0, 2).map((tier, i) => (
            <span key={i} className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-green-500/15 text-green-400' : 'bg-green-100 text-green-700'}`}>
              <Percent className="h-3 w-3" />
              {tier.percentage}%
            </span>
          ))}
          {task.profit_tiers.length > 2 && (
            <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>+{task.profit_tiers.length - 2}</span>
          )}
        </div>
      )}

      <p className={`text-sm mt-3 line-clamp-2 flex-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {language === 'ar' ? task.description_ar : task.description_en || task.description_ar}
      </p>

      <div className={`flex items-center gap-2 mt-4 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
        <button
          onClick={onView}
          className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid={`agent-task-view-${task.id}`}
        >
          {language === 'ar' ? 'التفاصيل' : 'Details'}
        </button>
        <button
          onClick={onStart}
          className={`flex-1 text-xs font-bold py-2 rounded-lg text-white bg-gradient-to-r ${accentGradient} hover:opacity-95`}
          data-testid={`agent-task-start-${task.id}`}
        >
          {isForm ? (language === 'ar' ? 'ابدأ' : 'Start') : (language === 'ar' ? 'ابدأ الآن' : 'Start now')}
        </button>
      </div>
    </div>
  );
}

function AgentIdCard({ agentInfo, language, isDarkMode, onPhotoChange, uploadingPhoto }) {
  // Always show the English full name on the card (per user requirement).
  // Fall back to other name fields only if English is not available.
  const name =
    agentInfo?.fullNameEn ||
    agentInfo?.full_name_en ||
    agentInfo?.fullName ||
    agentInfo?.account_name ||
    agentInfo?.fullNameAr ||
    'Agent';
  const bgm = agentInfo?.account_bgm_number || agentInfo?.bgm_number || '—';
  const agentType = agentInfo?.agent_type || 'silver';
  const typeLabel = { gold: language === 'ar' ? 'ذهبي' : 'GOLD', silver: language === 'ar' ? 'فضي' : 'SILVER', bronze: language === 'ar' ? 'برونزي' : 'BRONZE' }[agentType] || agentType;
  const photo = agentInfo?.profile_photo || '';

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f && onPhotoChange) onPhotoChange(f);
    e.target.value = '';
  };

  return (
    <div className="w-full h-full">
      {/* ID Card — matches the exact Cloud GM Business design template */}
      <div
        className="relative w-full h-full rounded-[28px] overflow-hidden"
        style={{
          aspectRatio: '856 / 539',
          background: 'radial-gradient(circle at 50% 30%, #1f2226 0%, #121417 60%, #0a0b0d 100%)',
          boxShadow: '0 30px 60px rgba(0,0,0,0.4), 0 10px 20px rgba(0,0,0,0.3)',
        }}
        data-testid="agent-id-card"
      >
        {/* Subtle cloudy texture overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(circle at 20% 80%, rgba(255,255,255,0.03) 0%, transparent 40%),
              radial-gradient(circle at 80% 20%, rgba(255,255,255,0.02) 0%, transparent 40%),
              radial-gradient(circle at 50% 50%, rgba(255,255,255,0.015) 0%, transparent 60%)
            `,
          }}
        />

        {/* ===== Card Content ===== */}
        <div className="relative h-full w-full flex flex-col justify-between text-white" style={{ padding: '5% 5.6%' }} dir="ltr">

          {/* Top Header Bar */}
          <div className="flex items-center justify-between w-full">
            <span className="font-extrabold uppercase text-white" style={{ fontSize: 'clamp(9px, 1.6vw, 14px)', letterSpacing: '0.09em', fontFamily: '"Montserrat", ui-sans-serif, system-ui, sans-serif' }}>
              CLOUD GM BUSINESS
            </span>
            <span className="font-extrabold uppercase text-white" style={{ fontSize: 'clamp(9px, 1.6vw, 14px)', letterSpacing: '0.09em', fontFamily: '"Montserrat", ui-sans-serif, system-ui, sans-serif' }}>
              AGENT CARD
            </span>
          </div>

          {/* Middle Body — LEFT: brand logo | RIGHT: agent name/label (large) */}
          <div className="flex items-center justify-between w-full flex-1 gap-4" style={{ paddingTop: '1%' }} dir="ltr">

            {/* LEFT: Brand logo (contains all brand text) */}
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: '38%' }}>
              <img
                src="/cloudgm-logo-full.png"
                alt="CloudGM Business"
                style={{
                  width: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(56,182,255,0.3))',
                }}
              />
            </div>

            {/* RIGHT: Agent info block (enlarged, name uses account_name as-is) */}
            <div className="min-w-0 flex-1 text-left">
              <p
                className="text-white/60 font-semibold leading-none uppercase"
                style={{
                  fontSize: 'clamp(10px, 1.5vw, 14px)',
                  letterSpacing: '0.28em',
                  marginBottom: '4%',
                  fontFamily: '"Montserrat", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                AGENT
              </p>
              <p
                className="font-extrabold text-white leading-tight uppercase truncate"
                style={{
                  fontSize: 'clamp(12px, 2.2vw, 20px)',
                  letterSpacing: '0.02em',
                }}
                data-testid="id-card-name"
              >
                {name}
              </p>
            </div>

            {/* Hidden file input (kept for future upload trigger, avatar removed) */}
            <input
              id="agent-photo-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              disabled={uploadingPhoto}
              data-testid="agent-photo-input"
            />
          </div>

          {/* Bottom bar — BGM number on the left corner (enlarged) */}
          <div className="flex items-center justify-start w-full">
            <span
              className="text-cyan-300 font-mono font-bold uppercase"
              style={{ fontSize: 'clamp(11px, 1.8vw, 14px)', letterSpacing: '0.15em', fontFamily: '"Montserrat", ui-monospace, monospace' }}
              data-testid="id-card-bgm-corner"
            >
              {bgm}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono, badge, valueClass, isDarkMode }) {
  return (
    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
      <p className={`text-[11px] uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-sm ${mono ? 'font-mono' : 'font-semibold'} ${badge ? 'uppercase' : ''} ${valueClass || (isDarkMode ? 'text-white' : 'text-gray-800')}`}>
        {value || '—'}
      </p>
    </div>
  );
}

import { fmtAED } from '../utils/currency';

function StatCard({ label, value, unit, color, isDarkMode }) {
  const colorMap = {
    green: { text: isDarkMode ? 'text-green-400' : 'text-green-600', bg: isDarkMode ? 'bg-[#1E293B]' : 'bg-green-50' },
    amber: { text: isDarkMode ? 'text-amber-400' : 'text-amber-600', bg: isDarkMode ? 'bg-[#1E293B]' : 'bg-amber-50' },
    blue: { text: isDarkMode ? 'text-blue-400' : 'text-blue-600', bg: isDarkMode ? 'bg-[#1E293B]' : 'bg-blue-50' },
    purple: { text: isDarkMode ? 'text-purple-400' : 'text-purple-600', bg: isDarkMode ? 'bg-[#1E293B]' : 'bg-purple-50' },
  }[color];
  // Show floored fils only for AED/monetary values. Non-currency counts render as-is.
  const isMoney = unit && (unit === 'AED' || unit === 'درهم');
  const displayValue = isMoney ? fmtAED(value) : value;
  return (
    <div className={`p-4 rounded-2xl text-center border ${colorMap.bg} ${isDarkMode ? 'border-white/10' : 'border-gray-200/70'}`}>
      <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-2xl font-bold ${colorMap.text}`}>{displayValue}</p>
      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{unit}</p>
    </div>
  );
}
